import SAAxios from '../../../http/requests/service-appointments';
import SARAxios from '../../../http/requests/service-appointments-reports';

export default {
    /**
     * Opens service appointment report modal.
     *
     * @param commit Vuex commit function
     * @param id Service Appointment ID
     */
    openReportModal ({ commit }, id) {
        commit('TOGGLE_REPORT_MODAL', {
            open: true,
            id: id,
        });
    },

    /**
     * Closes service appointment report modal.
     *
     * @param commit Vuex commit function
     */
    closeReportModal ({ commit }) {
        commit('TOGGLE_REPORT_MODAL', {
            open: false,
            id: null,
        });
    },

    /**
     * Retrieves service appointment and its report.
     *
     * @param commit Vuex commit function
     * @param id Service Appointment ID
     *
     * @returns {Promise<unknown>}
     */
    getServiceAppointmentReport ({ commit }, id) {
        return new Promise((resolve, reject) => {
            SAAxios.getServiceAppointment(id)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_REPORT_MODAL_DATA', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Creates new or updates existing service appointment report.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    createUpdateReport ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SARAxios.createUpdateReport(payload.report, payload.appointmentId, payload.isSubmit)
                .then(response => {
                    commit('SET_REPORT_MODAL_ERRORS', {});
                    commit('SET_REPORT_DATA', response.data.data);
                    commit('SET_APPOINTMENT_REPORT', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    if (error.response.status === 422) {
                        commit('SET_REPORT_MODAL_ERRORS', error.response.data.data);
                    }

                    reject(error);
                });
        });
    },

    /**
     * Submits service appointment report.
     *
     * @param commit Vuex commit function
     * @param appointmentId Service Appointment ID
     *
     * @returns {Promise<unknown>}
     */
    submitReport ({ commit }, appointmentId) {
        return new Promise((resolve, reject) => {
            SARAxios.submitReport(appointmentId)
                .then(response => {
                    commit('SET_REPORT_DATA', response.data.data);
                    commit('SET_APPOINTMENT_REPORT', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Approves service appointment report.
     *
     * @param commit Vuex commit function
     * @param appointmentId Service Appointment ID
     *
     * @returns {Promise<unknown>}
     */
    approveReport ({ commit }, appointmentId) {
        return new Promise((resolve, reject) => {
            SARAxios.approveReport(appointmentId)
                .then(response => {
                    commit('SET_REPORT_DATA', response.data.data);
                    commit('SET_APPOINTMENT_REPORT', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Declines service appointment report.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    declineReport ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SARAxios.declineReport(payload.id, payload.reason)
                .then(response => {
                    commit('SET_REPORT_DATA', response.data.data);
                    commit('SET_APPOINTMENT_REPORT', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    if (error.response.status === 422) {
                        commit('SET_ACTION_ERRORS', error.response.data.data);
                    }

                    reject(error);
                });
        });
    },

    /**
     * Cancels service appointment report.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    cancelReport ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SARAxios.cancelReport(payload.id, payload.reason)
                .then(response => {
                    commit('SET_REPORT_DATA', response.data.data);
                    commit('SET_APPOINTMENT_REPORT', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    if (error.response.status === 422) {
                        commit('SET_ACTION_ERRORS', error.response.data.data);
                    }

                    reject(error);
                });
        });
    },

    /**
     * Opens/closes service appointment report action with reason block.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    toggleActionWithReasonBlock ({ commit }, data) {
        commit('TOGGLE_ACTION_WITH_REASON', data);
    },

    /**
     * Sets action with reason, reason value.
     *
     * @param commit Vuex commit function
     * @param data Reason
     */
    setActionReason ({ commit }, data) {
        commit('SET_ACTION_REASON', data);
    },

    /**
     * Sets service appointment report modal data.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    setReportData ({ commit }, data) {
        commit('SET_REPORT_DATA', data);
    },

    /**
     * Toggles service appointment report checkbox value.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    toggleCheckbox ({ commit }, data) {
        commit('TOGGLE_CHECKBOX', data);
    },

    /**
     * Get service appointments report file upload URL.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getUploadUrl ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            SARAxios.getUploadUrl(payload.appointmentId, payload.fileName)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Adds file object to given field.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    addFile ({ commit }, data) {
        commit('ADD_FILE', data);
    },

    /**
     * Removes file object from given field.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    removeFile ({ commit }, data) {
        commit('REMOVE_FILE', data);
    },

    /**
     * Toggles is uploading indicator.
     *
     * @param state State object
     * @param isUploading Is uploading
     */
    toggleIsUploading ({ commit }, isUploading) {
        commit('TOGGLE_IS_UPLOADING', isUploading);
    },

    /**
     * Get service appointments report creation information.
     *
     * @param commit Vuex commit function
     * @param appointmentId Service Appointment ID
     *
     * @returns {Promise<unknown>}
     */
    getCreateInformation ({ commit }, appointmentId) {
        return new Promise((resolve, reject) => {
            SARAxios.getCreateInformation(appointmentId)
                .then(response => {
                    commit('SET_CREATE_INFORMATION', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Adds empty damage object.
     *
     * @param state Vuex state
     * @param commit Vuex commit function
     * @param dispatch Vuex dispatch function
     * @param data Data object
     */
    addDamage ({
        state,
        commit,
        dispatch,
    }, data) {
        return new Promise((resolve, reject) => {
            if (typeof state.examples.damage === 'undefined') {
                dispatch('toggleIsLoading', true);

                SARAxios.getDamageExample(data.appointmentId)
                    .then(response => {
                        commit('ADD_EXAMPLE', {
                            field: 'damage',
                            data: response.data.data,
                        });

                        commit('ADD_DAMAGE', data.id);

                        dispatch('toggleIsLoading', false);

                        resolve(response);
                    })
                    .catch(error => {
                        console.log(error);

                        dispatch('toggleIsLoading', false);

                        reject(error);
                    });
            } else {
                commit('ADD_DAMAGE', data.id);

                resolve();
            }
        });
    },

    /**
     * Adds empty damage object by given QR Code.
     *
     * @param state Vuex state
     * @param commit Vuex commit function
     * @param dispatch Vuex dispatch function
     * @param data Data object
     */
    addDamageByQrCode ({
        state,
        commit,
        dispatch,
    }, data) {
        return new Promise((resolve, reject) => {
            dispatch('toggleIsLoading', true);

            SARAxios.getDamageExample(data.appointmentId, data.qrCode)
                .then(response => {
                    commit('ADD_QR_DAMAGE', {
                        id: data.id,
                        damage: response.data.data,
                    });

                    dispatch('toggleIsLoading', false);

                    resolve(response);
                })
                .catch(error => {
                    console.log(error);

                    dispatch('toggleIsLoading', false);

                    reject(error);
                });
        });
    },

    /**
     * Removes damage.
     *
     * @param commit Vuex commit function
     * @param index Damage index
     */
    removeDamage ({ commit }, index) {
        commit('REMOVE_DAMAGE', index);
    },

    /**
     * Sets service appointment damage data.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    setDamageData ({ commit }, data) {
        commit('SET_DAMAGE_DATA', data);
    },

    /**
     * Sets open damage ID.
     *
     * @param commit Vuex commit function
     * @param id Damage ID
     */
    setOpenDamage ({ commit }, id) {
        commit('SET_OPEN_DAMAGE', id);
    },

    /**
     * Toggles is loading indicator.
     *
     * @param state State object
     * @param isLoading Is loading
     */
    toggleIsLoading ({ commit }, isLoading) {
        commit('TOGGLE_IS_LOADING', isLoading);
    },

    /**
     * Toggles service appointment report damage reference value.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    toggleDamageReference ({ commit }, data) {
        commit('TOGGLE_DAMAGE_REFERENCE', data);
    },

    /**
     * Toggles service appointment report damage reference values by given selected values.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    setDamageReferences ({ commit }, data) {
        commit('SET_DAMAGE_REFERENCES', data);
    },

    /**
     * Adds file object to given field.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    addDamageFile ({ commit }, data) {
        commit('ADD_DAMAGE_FILE', data);
    },

    /**
     * Removes file object from given field.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    removeDamageFile ({ commit }, data) {
        commit('REMOVE_DAMAGE_FILE', data);
    },

    /**
     * Adds new service appointment damage item.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    addDamageItem ({ commit }, data) {
        commit('ADD_DAMAGE_ITEM', data);
    },

    /**
     * Removes service appointment damage item.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    removeDamageItem ({ commit }, data) {
        commit('REMOVE_DAMAGE_ITEM', data);
    },

    /**
     * Sets service appointment damage item data.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    setDamageItemData ({ commit }, data) {
        commit('SET_DAMAGE_ITEM_DATA', data);
    },

    /**
     * Toggles confirmation modal data.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    toggleConfirmationModal ({ commit }, data) {
        commit('TOGGLE_CONFIRMATION_MODAL', data);
    },

    /**
     * Toggles service appointment report reference values by given selected values.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    setReferences ({ commit }, data) {
        commit('SET_REFERENCES', data);
    },

    /**
     * Sets open test ID.
     *
     * @param commit Vuex commit function
     * @param id Test ID
     */
    setOpenTest ({ commit }, id) {
        commit('SET_OPEN_TEST', id);
    },
    setOpenNote ({ commit }, id) {
        commit('SET_OPEN_NOTE', id);
    },
    /**
     * Adds empty test object.
     *
     * @param commit Vuex commit function
     * @param id Test ID
     */
    addTest ({ commit }, id) {
        commit('ADD_TEST', id);
    },
    addNote ({ commit }, id) {
        commit('ADD_NOTE', id);
    },

    /**
     * Removes test.
     *
     * @param commit Vuex commit function
     * @param index Test index
     */
    removeTest ({ commit }, index) {
        commit('REMOVE_TEST', index);
    },
    removeNote ({ commit }, index) {
        commit('REMOVE_NOTE', index);
    },

    /**
     * Sets service appointment test data.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    setTestData ({ commit }, data) {
        commit('SET_TEST_DATA', data);
    },
    setNoteData ({ commit }, data) {
        commit('SET_NOTE_DATA', data);
    },

    /**
     * Adds file object to given field.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    addTestFile ({ commit }, data) {
        commit('ADD_TEST_FILE', data);
    },
    addNoteFile ({ commit }, data) {
        commit('ADD_NOTE_FILE', data);
    },

    /**
     * Removes file object from given field.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    removeTestFile ({ commit }, data) {
        commit('REMOVE_TEST_FILE', data);
    },
    removeNoteFile ({ commit }, data) {
        commit('REMOVE_NOTE_FILE', data);
    },

    /**
     * Retrieves service appointment report PDF signed load url.
     *
     * @param _
     * @param id Service Appointment ID
     *
     * @returns {Promise<unknown>}
     */
    getOpenPdfUrl (_, id) {
        return new Promise((resolve, reject) => {
            SARAxios.getOpenPdfUrl(id)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Adds empty text with image object.
     *
     * @param state Vuex state
     * @param commit Vuex commit function
     * @param dispatch Vuex dispatch function
     * @param data Data object
     */
    addTextWithImage ({
        state,
        commit,
        dispatch,
    }, data) {
        commit('ADD_TEXT_WITH_IMAGE', data);
    },

    /**
     * Sets service appointment text with image data.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    setTextWithImageData ({ commit }, data) {
        commit('SET_TEXT_WITH_IMAGE_DATA', data);
    },
    /**
     * Adds file object to given field.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    addTextWithImageFile ({ commit }, data) {
        commit('ADD_TEXT_WITH_IMAGE_FILE', data);
    },

    /**
     * Removes file object from given field.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    removeTextWithImageFile ({ commit }, data) {
        commit('REMOVE_TEXT_WITH_IMAGE_FILE', data);
    },
    addReferral ({ commit }, id) {
        commit('ADD_REFERRAL', id);
    },
    setReferralData ({ commit }, data) {
        commit('SET_REFERRAL_DATA', data);
    },
    removeReferral({commit}, index) {
        commit('REMOVE_REFERRAL', index);
    },
    addReferralFile({commit}, data) {
        commit('ADD_REFERRAL_FILE', data);
    },
    removeReferralFile({commit}, data) {
        commit('REMOVE_REFERRAL_FILE', data);
    },
};
